import React from "react";
import { View, Text, StyleSheet, TouchableOpacity, Platform } from "react-native";
import { FontAwesomeIcon } from "../../platform";
import { COLORS as DEFAULT_COLORS } from "../theme/colors";
import { COLORS as APP_COLORS } from "../../theme/colors";

const COLORS = { ...DEFAULT_COLORS, ...APP_COLORS };

/**
 * Chip Component
 *
 * @param {Object} props - The properties passed to the Chip component.
 * @param {Object} props.icon - The specific icon to be displayed inside the chip.
 * @param {string} props.title - The text label for the chip.
 * @param {number} props.size - The size of the FontAwesomeIcon.
 * @param {string} [props.backgroundColor] - The background color for the chip (fallback: COLORS.backgroundGreen).
 * @param {string} [props.borderColor] - The border color for the chip (fallback: COLORS.primary).
 * @param {string} [props.iconColor] - The color of the icon inside the chip (fallback: COLORS.primary).
 * @param {function} [props.onPress] - The function to call when the chip is pressed.
 * @param {function} [props.type] - The type prop to specify the type of the chip info/button. the defaylt type is info which just displays text.
 * @param {string} [props.textColor] - The color of the text inside the chip (fallback: COLORS.text).
 * @param {string} [props.accessibilityLabel] - The accessibility label for the chip.
 * @param {string} [props.testID] - The test ID for testing purposes.
 *
 * @returns {JSX.Element} The rendered Chip component.
 */
const Chip = ({
  icon,
  title,
  size,
  backgroundColor,
  borderColor,
  iconColor,
  textColor,
  onPress,
  type = "info",
  accessibilityLabel,
  testID,
}) => {
  const ChipContent = (
    <View
      accessible={true}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
      style={[
        styles.chipContainer,
        {
          backgroundColor: COLORS[backgroundColor] || COLORS.background,
          borderColor: COLORS[borderColor] || COLORS.primary,
        },
      ]}
    >
      <FontAwesomeIcon icon={icon} size={size} color={COLORS[iconColor] || COLORS.primary} />
      <Text
        style={[
          styles.chipText,
          {
            color: COLORS[textColor] || COLORS.text,
          },
        ]}
      >
        {title}
      </Text>
    </View>
  );

  return type === "button" ? <TouchableOpacity onPress={onPress}>{ChipContent}</TouchableOpacity> : ChipContent;
};

const styles = StyleSheet.create({
  chipContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: Platform.OS == "web" ? 2 : 1.5,
    width: "auto",
    maxHeight: 50,
  },
  chipText: {
    fontSize: 14,
    fontWeight: "500",
    marginLeft: 8,
  },
});

export default Chip;
